import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import { toast } from 'react-hot-toast';

function BicomTool() {
  const [selectedEpbx, setSelectedEpbx] = useState('');
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [extensions, setExtensions] = useState([]);
  const [selectedExtension, setSelectedExtension] = useState('');
  const [extensionConfig, setExtensionConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [epbxConfigs, setEpbxConfigs] = useState({});

  useEffect(() => {
    const fetchEpbxConfigs = async () => {
      try {
        const response = await api.get('/bicom/configs');
        if (response.data.success) {
          setEpbxConfigs(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching EPBX configurations:', error);
        toast.error('Failed to load EPBX configurations');
      }
    };

    fetchEpbxConfigs();
  }, []);

  const resetFromLevel = (level) => {
    if (level <= 1) {
      setSelectedEpbx('');
      setTenants([]);
      setSelectedTenant('');
      setExtensions([]);
      setSelectedExtension('');
      setExtensionConfig(null);
    }
    if (level <= 2) {
      setSelectedTenant('');
      setExtensions([]);
      setSelectedExtension('');
      setExtensionConfig(null);
    }
    if (level <= 3) {
      setSelectedExtension('');
      setExtensionConfig(null);
    }
    if (level <= 4) {
      setExtensionConfig(null);
    }
  };

  const fetchTenants = async (epbxNumber) => {
    if (!epbxNumber) return;

    setLoading(true);
    try {
      const response = await api.get(`/bicom/tenants/${epbxNumber}`);
      console.log('Fetched tenants:', response.data.data);
      if (response.data.success && response.data.data) {
        setTenants(response.data.data);
        toast.success('Tenants loaded successfully');
      } else {
        toast.error('No tenants found');
      }
    } catch (error) {
      console.error('Error fetching tenants:', error);
      toast.error('Failed to fetch tenants');
    } finally {
      setLoading(false);
    }
  };

  const fetchExtensions = async (tenantServer) => {
    if (!selectedEpbx || !tenantServer) return;

    setLoading(true);
    try {
      const response = await api.get(`/bicom/extensions/${selectedEpbx}/${tenantServer}`);

      if (response.data.success && response.data.data) {
        setExtensions(response.data.data);
        toast.success('Extensions loaded successfully');
      } else {
        toast.error('No extensions found');
      }
    } catch (error) {
      console.error('Error fetching extensions:', error);

      if (error.response?.data?.message) {
        toast.error(`Extensions error: ${error.response.data.message}`);
      } else {
        toast.error('Failed to fetch extensions');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchExtensionConfig = async (extensionId, tenantServer) => {
    if (!selectedEpbx || !extensionId || !tenantServer) return;

    setLoading(true);
    try {
      const response = await api.get(`/bicom/extension-config/${selectedEpbx}/${tenantServer}/${extensionId}`);

      if (response.data.success && response.data.data) {
        setExtensionConfig(response.data.data);
        toast.success('Extension configuration loaded successfully');
      } else {
        toast.error('No configuration found');
      }
    } catch (error) {
      console.error('Error fetching extension configuration:', error);
      toast.error('Failed to fetch extension configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleEpbxChange = (epbxNumber) => {
    resetFromLevel(1);
    setSelectedEpbx(epbxNumber);
    if (epbxNumber) {
      fetchTenants(epbxNumber);
    }
  };

  const handleTenantChange = (tenant) => {
    resetFromLevel(2);
    setSelectedTenant(tenant);
    console.log('Selected tenant:', tenant);
    if (tenant) {
      // Use the tenant's server property or fallback to tenantcode if server doesn't exist
      const serverParam = tenant.id;
      console.log('Using server parameter:', serverParam);
      fetchExtensions(serverParam);
    }
  };

  const handleExtensionChange = (extension) => {
    resetFromLevel(3);
    setSelectedExtension(extension);
    if (extension && selectedTenant) {
      const serverParam = selectedTenant.id;
      fetchExtensionConfig(extension.id, serverParam);
    }
  };

  return (
    <div className="h-full bg-gray-100 p-4 flex flex-col items-center overflow-y-auto">
      <div className="w-full max-w-4xl bg-white shadow-lg rounded-xl p-6">
        <h1 className="text-3xl font-bold text-center mb-6 text-blue-600">
          Bicom API Tool
        </h1>

        {/* EPBX Server Selector */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <label htmlFor="epbx-selector" className="block text-sm font-medium text-gray-700">
              Select EPBX Server:
            </label>
            <button
              onClick={() => resetFromLevel(1)}
              className="text-red-500 hover:text-red-700 text-sm"
              title="Reset selection"
            >
              ✕ Reset
            </button>
          </div>
          <select
            id="epbx-selector"
            value={selectedEpbx}
            onChange={(e) => handleEpbxChange(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-4 py-2"
            disabled={loading}
          >
            <option value="">Choose EPBX Server...</option>
            {Object.keys(epbxConfigs).map((epbxNum) => (
              <option key={epbxNum} value={epbxNum}>
                {epbxConfigs[epbxNum].name} ({epbxConfigs[epbxNum].host})
              </option>
            ))}
          </select>
        </div>

        {/* Tenant Selector */}
        {tenants.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <label htmlFor="tenant-selector" className="block text-sm font-medium text-gray-700">
                Select Tenant:
              </label>
              <button
                onClick={() => resetFromLevel(2)}
                className="text-red-500 hover:text-red-700 text-sm"
                title="Reset from this level"
              >
                ✕ Reset
              </button>
            </div>
            <select
              id="tenant-selector"
              value={selectedTenant ? selectedTenant.id : ''}
              onChange={(e) => {
                const tenant = tenants.find(t => t.id === e.target.value);
                handleTenantChange(tenant);
              }}
              className="w-full border border-gray-300 rounded-md px-4 py-2"
              disabled={loading}
            >
              <option value="">Choose Tenant...</option>
              {tenants.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name} {/* (Server: { tenant.id}) */}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Extension Selector */}
        {extensions.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <label htmlFor="extension-selector" className="block text-sm font-medium text-gray-700">
                Select Extension:
              </label>
              <button
                onClick={() => resetFromLevel(3)}
                className="text-red-500 hover:text-red-700 text-sm"
                title="Reset from this level"
              >
                ✕ Reset
              </button>
            </div>
            <select
              id="extension-selector"
              value={selectedExtension ? selectedExtension.id : ''}
              onChange={(e) => {
                const extension = extensions.find(ext => ext.id === e.target.value);
                handleExtensionChange(extension);
              }}
              className="w-full border border-gray-300 rounded-md px-4 py-2"
              disabled={loading}
            >
              <option value="">Choose Extension...</option>
              {extensions.map((extension) => (
                <option key={extension.id} value={extension.id}>
                  {extension.extension} - {extension.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Extension Configuration Display */}
        {extensionConfig && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Extension Configuration
              </h3>
              <button
                onClick={() => resetFromLevel(4)}
                className="text-red-500 hover:text-red-700 text-sm"
                title="Clear configuration"
              >
                ✕ Clear
              </button>
            </div>
            <div className="bg-gray-50 p-4 rounded-md shadow-inner max-h-96 overflow-y-auto">
              <pre className="text-sm text-gray-800 whitespace-pre-wrap">
                {JSON.stringify(extensionConfig, null, 2)}
              </pre>
            </div>
          </div>
        )}

        {/* Loading Indicator */}
        {loading && (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading...</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default BicomTool;